<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipTypeList } from '../../item/data'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { getRefineGoldPrice, getRefineStonePrice } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import type { RefineRes } from '@/types/smithy'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage } from 'element-plus'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'
import type { UserAttrType } from '@/types/user'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (!item.supremeAttrs) return false
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

function handleChoice(item: EquipItemType | undefined) {
  if (item && !item.supremeAttrs) return ElMessage.warning('仅至臻装备可精造')
  equipItemId.value = item?.id || ''
}

const resShow = ref(false)
async function handleRefine() {
  if (!equipInfo.value) return
  const { id, refineAttr = [] } = equipInfo.value
  const price = getRefineGoldPrice(refineAttr[0])
  const stone = getRefineStonePrice(refineAttr[0])
  if (price > userStore.gold) return ElMessage.warning('您的金币不足')
  if (userStore.consumables.supremeRefineStone < stone) return ElMessage.warning('您的至臻精造石不足')

  const { refineAttr: resRefineAttr, userAttr } = await socketEmitAsync<RefineRes>({ type: 'supremeRefineEquip', data: { id } })
  userStore.userAttr = userAttr

  const item = userStore.itemList.find((item) => item.id === id)
  if (!item || !equipInfo.value) return
  item.refineAttr = resRefineAttr
  equipInfo.value.refineAttr = resRefineAttr

  resShow.value = true

  userStore.gold -= price
  userStore.consumables.supremeRefineStone -= stone
}
function handleResetRefine() {
  if (!equipInfo.value) return
  const { id, quality, name } = equipInfo.value

  ElMessageBox.confirm(
    `<p class="affix">确定重置<span class="${quality}">${name}</span>吗？（该操作将<span class="shrink">重置</span>精造等级，请务必谨慎）<br />需花费<span class="diamond">800 钻石</span></p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const userAttr = await socketEmitAsync<UserAttrType>({ type: 'resetEquipRefine', data: { id } })

      const item = userStore.itemList.find((item) => item.id === id)
      if (!item || !equipInfo.value) return
      item.refineAttr = undefined
      equipInfo.value.refineAttr = undefined

      userStore.userAttr = userAttr
      userStore.diamond -= 800
    })
    .catch(() => {})
}
</script>

<template>
  <div class="enchant">
    <div class="enchant-table border-wrap">
      <h4>锻造台</h4>
      <div class="enchant-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>精造说明</h4>
            <p>1. 通过<span class="supreme-simple">无垠之塔</span>等挑战，可使用<span class="supreme-simple">至臻精造石</span>对装备进行精造。</p>
            <p>
              2. 精造后将获得<span class="grow">全伤害加成</span>及<span class="grow">抗魔值</span>，每
              <span class="info">5</span>级精造，将触发一次<span class="info">精造命中</span>。
            </p>
            <p>
              3. 精造命中为随机一条至臻属性（刻印属性除外）中的基础属性进行加成，命中的属性将获得<span class="grow">30%</span
              >加成，多次命中同一词条时获得额外加成：2次<span class="grow">80%</span>，3次<span class="grow">140%</span>。
            </p>
            <p>4. 随精造等级增加，所需<span class="gold">金币</span>及<span class="supreme-simple">至臻精造石</span>也会增加。</p>
            <p>
              5. 若您对精造结果不满意，可使用<span class="info">重置</span>功能，该操作将重置<span class="info">精造等级</span>（不返还材料、金币）。
            </p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <p>精造等级：{{ equipInfo.refineAttr?.[0] || 0 }}</p>
          <p>精造花费：{{ formatNumberWithCommas(getRefineGoldPrice(equipInfo.refineAttr?.[0])) }} 金币</p>
          <p style="text-indent: 5em">{{ getRefineStonePrice(equipInfo.refineAttr?.[0]) }} 至臻精造石</p>
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleRefine" :disabled="equipInfo.refineAttr?.[0] === 15">
            {{ equipInfo.refineAttr?.[0] === 15 ? '已达精造上限' : '开始精造' }}
          </el-button>
          <el-button type="danger" plain @click="handleResetRefine" :disabled="!equipInfo.refineAttr?.[0]" style="margin: 10px 0 0 0">
            重置
          </el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, index)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package">
      <h4>背包</h4>
      <p class="cur-gold">
        <span>💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
        <span style="margin-left: 10px">🟨 {{ formatNumberWithCommas(userStore.consumables.supremeRefineStone) }}</span>
      </p>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoice(item)" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>

    <el-dialog v-model="resShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">精造成功</span>
      </h2>
      <template v-if="equipInfo">
        <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
          <template #reference>
            <div style="width: 50%; margin: 0 auto">
              <CommonBtn size="small" style="width: 100%">
                <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
              </CommonBtn>
            </div>
          </template>
          <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 60vh" />
        </el-popover>
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="resShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.enchant {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .enchant-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .enchant-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
